<template>
  <div class="crm-logo">
    <div
      ref="box"
      class="crm-logo__box">
      <img
        class="crm-logo__layer crm-logo__mark"
        :class="{
          'crm-logo__mark--home': !isFleeing,
          'crm-logo__mark--loose': !!frame,
        }"
        :style="markStyle"
        src="@/assets/img/logo_hart_estate_mark.svg"
        alt="Logo"
        draggable="false">
      <brand-wordmark class="crm-logo__layer" />
    </div>
  </div>
</template>

<script setup>
import {
  computed, onBeforeUnmount, onMounted, reactive, ref,
} from 'vue';
import BrandWordmark from '@/Components/Common/BrandWordmark.vue';

// домик занимает 35 из 174 единиц исходного логотипа — отсюда его центр и ширина
const MARK_WIDTH_RATIO = 35 / 174;
const MARK_CENTER_X_RATIO = 17.5 / 174;
const MARK_CENTER_Y_RATIO = 18 / 35;

// расстояние считается до прямоугольника самого домика, а не до центра логотипа,
// поэтому радиус крошечный: срабатывает только когда курсор уже на домике
const FLEE_RADIUS = 10;
// логотип в полную величину — 190px; всё, что уже, значит сайдбар свёрнут
const FULL_SIZE_WIDTH = 150;
const RETURN_RADIUS = 150;
const STEP = 46;
const EDGE_GAP = 8;
const DEAD_END = 24;
const FREE_ROOM = 140;
const RETURN_MS = 900;
const IDLE_MS = 2500;

const box = ref(null);
const isFleeing = ref(false);
const offset = reactive({ x: 0, y: 0 });
const direction = reactive({ x: 1, y: 1 });
// пока домик бегает, он живёт в position: fixed — иначе его обрежет сайдбар.
// frame — то место на экране, где он стоял в вёрстке, от него считаются координаты
const frame = ref(null);

let settleTimer = null;
let idleTimer = null;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
const roomAhead = (way, value, min, max) => (way > 0 ? max - value : value - min);

const markStyle = computed(() => {
  const transform = { transform: `translate(${offset.x}px, ${offset.y}px)` };

  if (!frame.value) return transform;

  return {
    ...transform,
    position: 'fixed',
    left: `${frame.value.left}px`,
    top: `${frame.value.top}px`,
    width: `${frame.value.width}px`,
    height: `${frame.value.height}px`,
  };
});

const goHome = () => {
  clearTimeout(idleTimer);

  if (offset.x === 0 && offset.y === 0) return;

  isFleeing.value = false;
  direction.x = 1;
  direction.y = 1;
  offset.x = 0;
  offset.y = 0;

  // из fixed возвращаемся в поток только когда домик добежал до места
  clearTimeout(settleTimer);
  settleTimer = setTimeout(() => { frame.value = null; }, RETURN_MS);
};

// границы побега — весь экран, а не коробка логотипа
const bounds = (home, markWidth, markHeight) => {
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

  return {
    minX: EDGE_GAP - home.left,
    maxX: viewportWidth - home.left - markWidth - EDGE_GAP,
    minY: EDGE_GAP - home.top,
    maxY: viewportHeight - home.top - markHeight - EDGE_GAP,
  };
};

const onMove = (event) => {
  const rect = box.value?.getBoundingClientRect();

  // свёрнутый сайдбар ужимает логотип (а на мобильном схлопывает в ноль). в таком виде
  // домик трогать нельзя: он в position: fixed выпрыгнул бы поверх страницы из-под обрезки.
  // играем только когда логотип показан целиком
  if (!rect || rect.width < FULL_SIZE_WIDTH) {
    goHome();

    return;
  }

  const home = frame.value ?? {
    left: rect.left, top: rect.top, width: rect.width, height: rect.height,
  };
  const markWidth = home.width * MARK_WIDTH_RATIO;
  const centerX = home.left + (home.width * MARK_CENTER_X_RATIO) + offset.x;
  const centerY = home.top + (home.height * MARK_CENTER_Y_RATIO) + offset.y;
  const dx = centerX - event.clientX;
  const dy = centerY - event.clientY;

  // до прямоугольника домика, а не до его центра: иначе он шарахается от соседних пунктов меню
  const nearestX = clamp(event.clientX, centerX - (markWidth / 2), centerX + (markWidth / 2));
  const nearestY = clamp(event.clientY, centerY - (home.height / 2), centerY + (home.height / 2));
  const distance = Math.hypot(event.clientX - nearestX, event.clientY - nearestY);

  if (distance > RETURN_RADIUS) {
    goHome();

    return;
  }

  if (distance > FLEE_RADIUS) return;

  const limits = bounds(home, markWidth, home.height);

  // бежать некуда (окно схлопнуто или размеры ещё неизвестны) — лучше остаться на месте
  if (limits.maxX < limits.minX || limits.maxY < limits.minY) return;

  const isStart = !frame.value;

  if (isStart) {
    clearTimeout(settleTimer);
    frame.value = {
      left: rect.left, top: rect.top, width: rect.width, height: rect.height,
    };
  }

  // логотип живёт в углу экрана, поэтому «просто убегать от курсора» его там и запирает,
  // а на каждом событии он дёргается туда-обратно. вместо этого направление по оси —
  // состояние: сначала честно от курсора, у стены разворот и проскок мимо руки,
  // и обратно к «от курсора» только когда впереди снова открытое пространство
  const steer = (axis, position, min, max, cursor, center) => {
    const away = Math.sign(center - cursor) || direction[axis];

    if (isStart) {
      direction[axis] = roomAhead(away, position, min, max) >= DEAD_END ? away : -away;

      return;
    }

    if (roomAhead(direction[axis], position, min, max) < DEAD_END) {
      direction[axis] = -direction[axis];
    } else if (away !== direction[axis] && roomAhead(away, position, min, max) > FREE_ROOM) {
      direction[axis] = away;
    }
  };

  steer('x', offset.x, limits.minX, limits.maxX, event.clientX, centerX);
  steer('y', offset.y, limits.minY, limits.maxY, event.clientY, centerY);

  // чем ближе курсор, тем длиннее шаг — за счёт css-перехода это выглядит как бег, а не телепорт.
  // по «своей» оси домик бежит охотнее: так он уходит по диагонали, а не строго по прямой
  const push = (FLEE_RADIUS - distance) / FLEE_RADIUS;
  const weightX = Math.abs(dx) / (Math.abs(dx) + Math.abs(dy) || 1);

  isFleeing.value = true;
  offset.x = clamp(offset.x + (direction.x * STEP * push * (0.45 + weightX)), limits.minX, limits.maxX);
  offset.y = clamp(offset.y + (direction.y * STEP * push * (1.45 - weightX)), limits.minY, limits.maxY);

  clearTimeout(idleTimer);
  idleTimer = setTimeout(goHome, IDLE_MS);
};

onMounted(() => {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  window.addEventListener('mousemove', onMove, { passive: true });
  window.addEventListener('blur', goHome);
  document.addEventListener('mouseleave', goHome);
});

onBeforeUnmount(() => {
  clearTimeout(settleTimer);
  clearTimeout(idleTimer);
  window.removeEventListener('mousemove', onMove);
  window.removeEventListener('blur', goHome);
  document.removeEventListener('mouseleave', goHome);
});
</script>

<style scoped>
.crm-logo {
  display: flex;
  align-items: center;
  height: 100%;
}

.crm-logo__box {
  position: relative;
  width: 100%;
  max-width: 190px;
  aspect-ratio: 174 / 35;
}

.crm-logo__layer {
  position: absolute;
  top: 0;
  left: 0;
  display: block;
  width: 100%;
  height: 100%;
  user-select: none;
}

.crm-logo__mark {
  z-index: 1;
  transition: transform 260ms cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;
}

.crm-logo__mark--home {
  transition: transform 900ms cubic-bezier(0.33, 1, 0.68, 1);
}

/* убежавший домик не должен ни перехватывать клики, ни прятаться под контентом */
.crm-logo__mark--loose {
  z-index: 900;
  pointer-events: none;
}
</style>
